import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import {
  Sales, Tasks, Stats, Sla, Manager, Accounts as AccountsAPI,
  BackOffice as BackOfficeAPI, Spares as SparesAPI, DateRanges,
  Delivery,
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { SaleStatusPill, Pill } from "../components/StatusPill";
import { ROLE_LABELS } from "../lib/constants";
import {
  Plus, Wrench, Banknote, Package, Truck, ArrowRight,
  Activity, ClipboardList, BadgeCheck, FileText, ShieldCheck,
  Calendar as CalendarIcon, Users as UsersIcon, MapPin,
  Timer, AlertTriangle, AlertOctagon, Pause, CalendarClock,
  CircleAlert, Zap, ChevronRight,
} from "lucide-react";
import { cn } from "../lib/utils";

function MetricCard({ label, value, icon: Icon, tone = "primary", testid, to }) {
  const tones = {
    primary: "bg-primary/5 text-primary border-primary/10",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    sky: "bg-sky-50 text-sky-700 border-sky-100",
  };
  const inner = (
    <Card data-testid={testid} className="border-slate-200 transition-all hover:-translate-y-0.5 hover:shadow-md">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
              {label}
            </div>
            <div className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900">
              {value ?? "—"}
            </div>
          </div>
          <div className={`flex h-10 w-10 items-center justify-center rounded-md border ${tones[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

const RANGE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "this_week", label: "This Week" },
  { key: "this_month", label: "This Month" },
  { key: "custom", label: "Custom" },
  { key: "all_time", label: "All Time" },
];

function rangeFor(key, custom) {
  if (key === "today") return DateRanges.today();
  if (key === "this_week") return DateRanges.thisWeek();
  if (key === "this_month") return DateRanges.thisMonth();
  if (key === "custom") {
    // Custom range needs both ends populated. Until then we behave like
    // "all time" so the dashboard keeps showing live data while the user
    // is mid-selection.
    if (custom?.from && custom?.to) {
      return { date_from: custom.from, date_to: custom.to };
    }
    return DateRanges.allTime();
  }
  return DateRanges.allTime();
}

function DateRangeBar({ value, onChange, custom, onCustomChange }) {
  // Draft state lives locally so the user can edit From/To without firing
  // a network request on every keystroke. It's initialised from the parent
  // exactly once — the Clear button below explicitly resets both halves so
  // we never need to sync back from the parent state.
  const [draftFrom, setDraftFrom] = useState(custom?.from || "");
  const [draftTo, setDraftTo] = useState(custom?.to || "");
  const showPicker = value === "custom";
  const apply = () => {
    if (!draftFrom || !draftTo) return;
    onCustomChange({ from: draftFrom, to: draftTo });
  };
  const clear = () => {
    setDraftFrom(""); setDraftTo("");
    onCustomChange({ from: "", to: "" });
  };
  return (
    <div className="flex flex-col items-end gap-2" data-testid="dashboard-range-bar">
      <div className="flex items-center gap-2">
        <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
        {RANGE_PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => onChange(p.key)}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              value === p.key
                ? "bg-primary text-primary-foreground"
                : "text-slate-600 hover:bg-slate-100",
            )}
            data-testid={`range-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {showPicker && (
        <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1" data-testid="range-custom-picker">
          <input
            type="date"
            value={draftFrom}
            max={draftTo || undefined}
            onChange={(e) => setDraftFrom(e.target.value)}
            className="rounded border border-slate-200 px-1.5 py-0.5 text-xs"
            data-testid="range-custom-from"
          />
          <span className="text-xs text-slate-400">→</span>
          <input
            type="date"
            value={draftTo}
            min={draftFrom || undefined}
            onChange={(e) => setDraftTo(e.target.value)}
            className="rounded border border-slate-200 px-1.5 py-0.5 text-xs"
            data-testid="range-custom-to"
          />
          <button
            type="button"
            onClick={apply}
            disabled={!draftFrom || !draftTo}
            className="rounded-md bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground disabled:opacity-40"
            data-testid="range-custom-apply"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={clear}
            className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 hover:bg-slate-200"
            data-testid="range-custom-clear"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { activeUser, activeLocationIds, locations } = useApp();
  const navigate = useNavigate();
  const [rangeKey, setRangeKey] = useState("this_month");
  const [customRange, setCustomRange] = useState({ from: "", to: "" });

  const locParam = useMemo(() => activeLocationIds.join(","), [activeLocationIds]);
  const range = useMemo(
    () => rangeFor(rangeKey, customRange),
    [rangeKey, customRange],
  );

  const role = activeUser?.role;
  if (!activeUser) return null;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <DashboardHeader
        role={role}
        name={activeUser.name}
        activeLocationIds={activeLocationIds}
        rangeKey={rangeKey}
        onRangeChange={setRangeKey}
        customRange={customRange}
        onCustomChange={setCustomRange}
      />

      {role === "Admin" && <AdminDashboard locParam={locParam} range={range} locations={locations} />}
      {role === "DSE" && <DseDashboard user={activeUser} locParam={locParam} range={range} locations={locations} />}
      {role === "PDI" && <PdiDashboard locParam={locParam} range={range} />}
      {role === "Accounts" && <AccountsDashboard locParam={locParam} range={range} />}
      {role === "BackOffice" && <BackOfficeDashboard locParam={locParam} range={range} />}
      {role === "Spares" && <SparesDashboard locParam={locParam} range={range} />}
      {role === "SalesManager" && (
        <SalesManagerDashboard user={activeUser} locParam={locParam} range={range} locations={locations} />
      )}
    </div>
  );
}

function DashboardHeader({ role, name, activeLocationIds, rangeKey, onRangeChange, customRange, onCustomChange }) {
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
          {ROLE_LABELS[role]} · Dashboard
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
          Hi, {name?.split("(")[0].trim()}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {role === "DSE" && "Your sales, your pending tasks — at a glance."}
          {role === "SalesManager" && "Team performance across your assigned sales executives."}
          {role === "Admin" && `Live view of dealership operations across ${activeLocationIds.length} location${activeLocationIds.length === 1 ? "" : "s"}.`}
          {role === "PDI" && "Pending vehicle readiness work."}
          {role === "Accounts" && "Invoicing and ledger pending items."}
          {role === "BackOffice" && "Insurance, Road Tax, HSRP and VAS pendency."}
          {role === "Spares" && "Accessory readiness pendency."}
        </p>
      </div>
      <div className="flex flex-col items-start gap-2 lg:items-end">
        <DateRangeBar
          value={rangeKey}
          onChange={onRangeChange}
          custom={customRange}
          onCustomChange={onCustomChange}
        />
      </div>
    </div>
  );
}

// ───────── Admin ─────────

function AdminDashboard({ locParam, range, locations }) {
  const [stats, setStats] = useState(null);
  const [slaStats, setSlaStats] = useState(null);
  const [deptPendency, setDeptPendency] = useState([]);
  const [actions, setActions] = useState([]);
  const [workflow, setWorkflow] = useState(null);
  const [dueToday, setDueToday] = useState([]);

  useEffect(() => {
    (async () => {
      const params = { location_ids: locParam, ...range };
      const [s, sla, dept, act, wf, del] = await Promise.all([
        Stats.get(params),
        Sla.dashboard({ location_ids: locParam }),
        Stats.departmentPendency({ location_ids: locParam }),
        Stats.actionRequired({ location_ids: locParam }),
        Stats.workflowSummary({ location_ids: locParam }),
        Delivery.list({ location_ids: locParam }),
      ]);
      setStats(s); setSlaStats(sla);
      setDeptPendency(dept);
      setActions(act);
      setWorkflow(wf);
      setDueToday(filterDueToday(del || []));
    })();
  }, [locParam, range]);

  return (
    <>
      {stats && <KpiSection stats={stats} scope="admin" />}
      {slaStats && <SlaSummary slaStats={slaStats} />}

      <DepartmentPendencyGrid rows={deptPendency} />

      <DeliveriesDueTodayCard rows={dueToday} locations={locations} />

      {/* Action Required + Workflow Summary side-by-side on desktop, stacked on mobile */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ActionRequiredCard actions={actions} className="lg:col-span-1" />
        <WorkflowSummaryCard workflow={workflow} className="lg:col-span-2" />
      </div>
    </>
  );
}

// ───────── DSE ─────────

function DseDashboard({ user, locParam, range, locations }) {
  const [stats, setStats] = useState(null);
  const [slaStats, setSlaStats] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [todayDeliveries, setTodayDeliveries] = useState([]);

  useEffect(() => {
    (async () => {
      const params = { location_ids: locParam, dse_id: user.id, ...range };
      const today = new Date().toISOString().slice(0, 10);
      const [s, sla, sales, deliv] = await Promise.all([
        Stats.get(params),
        Sla.dashboard({ location_ids: locParam }),
        Sales.list(params),
        Sales.list({ location_ids: locParam, dse_id: user.id,
                     status: "ready_for_delivery,delivered" }),
      ]);
      setStats(s); setSlaStats(sla);
      setRecentSales(sales.slice(0, 6));
      // Filter for "promised today"
      setTodayDeliveries(
        deliv.filter((d) => (d.delivery?.promised_date || "").startsWith(today)).slice(0, 6),
      );
    })();
  }, [user.id, locParam, range]);

  const locMap = useMemo(
    () => Object.fromEntries(locations.map((l) => [l.id, l])),
    [locations],
  );

  return (
    <>
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <MetricCard label="My Sales" value={stats.total_sales} icon={FileText} tone="primary" testid="metric-my-sales" />
          <MetricCard label="In Progress" value={stats.in_progress} icon={Activity} tone="amber" testid="metric-in-progress" />
          <MetricCard label="Ready for Delivery" value={stats.ready_for_delivery} icon={BadgeCheck} tone="emerald" testid="metric-ready" />
          <MetricCard label="Delivered" value={stats.delivered} icon={Truck} tone="emerald" testid="metric-delivered" />
          <MetricCard label="Drafts" value={stats.drafts} icon={ClipboardList} tone="slate" testid="metric-drafts" />
        </div>
      )}

      {slaStats && (
        <SlaSummary slaStats={slaStats} title="My SLA · Across Locations" />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <RecentSalesCard sales={recentSales} locMap={locMap} title="My Recent Sales" className="lg:col-span-2" />
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="font-display text-lg">Deliveries Today</CardTitle>
            <span className="text-xs font-medium text-slate-400">{todayDeliveries.length}</span>
          </CardHeader>
          <CardContent className="p-0">
            {todayDeliveries.length === 0 ? (
              <EmptyRow message="No deliveries scheduled for today." />
            ) : (
              <ul className="divide-y divide-border">
                {todayDeliveries.map((s) => (
                  <li key={s.id} className="px-5 py-3">
                    <Link to={`/sales/${s.id}`} className="block">
                      <div className="font-mono-data text-xs text-slate-900">{s.sr_number}</div>
                      <div className="text-xs text-slate-500">
                        {s.customer?.name} · {locMap[s.location_id]?.code}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

// ───────── PDI ─────────

function PdiDashboard({ locParam, range }) {
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    (async () => {
      const [s, t] = await Promise.all([
        Stats.get({ location_ids: locParam, ...range }),
        Tasks.list({ role: "PDI", status: "pending", location_ids: locParam }),
      ]);
      setStats(s); setTasks(t.slice(0, 10));
    })();
  }, [locParam, range]);

  return (
    <>
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <MetricCard label="VIN Pending" value={stats.pending_pdi_vin} icon={Wrench} tone="amber" testid="metric-vin-pending" to="/pdi" />
          <MetricCard label="Ready for Delivery" value={stats.ready_for_delivery} icon={BadgeCheck} tone="emerald" testid="metric-ready" />
          <MetricCard label="Pending Check Sheets" value={stats.pending_pdi_ready} icon={ClipboardList} tone="primary" testid="metric-check-sheets" to="/pdi" />
        </div>
      )}
      <PendingTasksCard tasks={tasks} title="My Pending Tasks" />
    </>
  );
}

// ───────── Accounts ─────────

function AccountsDashboard({ locParam, range }) {
  const [stats, setStats] = useState(null);
  const [acctsStats, setAcctsStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    (async () => {
      const [s, a, t] = await Promise.all([
        Stats.get({ location_ids: locParam, ...range }),
        AccountsAPI.stats({ location_ids: locParam }),
        Tasks.list({ role: "Accounts", status: "pending", location_ids: locParam }),
      ]);
      setStats(s); setAcctsStats(a); setTasks(t.slice(0, 10));
    })();
  }, [locParam, range]);

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <MetricCard label="Pending Verification"
          value={acctsStats?.pending_verification ?? "—"}
          icon={ShieldCheck} tone="amber" testid="metric-pending-verification" to="/accounts" />
        <MetricCard label="Invoice Pending"
          value={stats?.pending_accounts}
          icon={Banknote} tone="rose" testid="metric-invoice-pending" to="/accounts" />
        <MetricCard label="Transit Hold"
          value={stats?.transit_hold} icon={Pause} tone="sky" testid="metric-transit-hold" />
      </div>
      <PendingTasksCard tasks={tasks} title="Pending Accounts Tasks" />
    </>
  );
}

// ───────── Back Office ─────────

function BackOfficeDashboard({ locParam, range }) {
  const [bo, setBo] = useState(null);
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    (async () => {
      const [b, t] = await Promise.all([
        BackOfficeAPI.stats({ location_ids: locParam }),
        Tasks.list({ role: "BackOffice", status: "pending", location_ids: locParam }),
      ]);
      setBo(b); setTasks(t.slice(0, 10));
    })();
  }, [locParam, range]);

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard label="Insurance Pending" value={bo?.pending_insurance} icon={ShieldCheck} tone="primary" testid="metric-insurance" />
        <MetricCard label="Road Tax Pending" value={bo?.pending_road_tax} icon={Banknote} tone="amber" testid="metric-road-tax" />
        <MetricCard label="HSRP Pending" value={bo?.pending_hsrp} icon={BadgeCheck} tone="rose" testid="metric-hsrp" />
        <MetricCard label="VAS Pending" value={bo?.pending_vas} icon={Package} tone="sky" testid="metric-vas" />
      </div>
      <PendingTasksCard tasks={tasks} title="Pending Back Office Tasks" />
    </>
  );
}

// ───────── Spares ─────────

function SparesDashboard({ locParam, range }) {
  const [sp, setSp] = useState(null);
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    (async () => {
      const [s, t] = await Promise.all([
        SparesAPI.stats({ location_ids: locParam }),
        Tasks.list({ role: "Spares", status: "pending", location_ids: locParam }),
      ]);
      setSp(s); setTasks(t.slice(0, 10));
    })();
  }, [locParam, range]);

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <MetricCard label="Pending Accessories" value={sp?.pending} icon={Package} tone="amber" testid="metric-pending-acc" to="/spares" />
        <MetricCard label="Ready Items" value={sp?.ready} icon={BadgeCheck} tone="emerald" testid="metric-ready-acc" />
        <MetricCard label="Not Available" value={sp?.not_available} icon={AlertOctagon} tone="rose" testid="metric-na-acc" />
      </div>
      <PendingTasksCard tasks={tasks} title="Pending Spares Tasks" />
    </>
  );
}

// ───────── Sales Manager ─────────

function SalesManagerDashboard({ user, locParam, range, locations }) {
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState(null);
  const [actions, setActions] = useState([]);
  const [workflow, setWorkflow] = useState(null);
  const [deptPendency, setDeptPendency] = useState([]);
  const [dueToday, setDueToday] = useState([]);

  useEffect(() => {
    (async () => {
      const params = { location_ids: locParam, manager_id: user.id, ...range };
      const [s, perf, act, wf, dept, del] = await Promise.all([
        Stats.get(params),
        Manager.teamPerformance(user.id, { location_ids: locParam, ...range }),
        Stats.actionRequired({ location_ids: locParam }),
        Stats.workflowSummary({ location_ids: locParam }),
        Stats.departmentPendency({ location_ids: locParam }),
        Delivery.list({ location_ids: locParam }),
      ]);
      setStats(s); setTeam(perf);
      setActions(act); setWorkflow(wf); setDeptPendency(dept);
      setDueToday(filterDueToday(del || []));
    })();
  }, [user.id, locParam, range]);

  return (
    <>
      {stats && <KpiSection stats={stats} scope="manager" />}

      <DepartmentPendencyGrid rows={deptPendency} />

      <DeliveriesDueTodayCard rows={dueToday} locations={locations} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ActionRequiredCard actions={actions} className="lg:col-span-1" />
        <WorkflowSummaryCard workflow={workflow} className="lg:col-span-2" />
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <UsersIcon className="h-4 w-4 text-primary" />
            <CardTitle className="font-display text-lg">Team Performance</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!team ? (
            <div className="px-5 py-6 text-sm text-slate-400">Loading team data…</div>
          ) : team.rows.length === 0 ? (
            <EmptyRow message="No Sales Executives are reporting to you yet. Ask your Admin to map DSEs to you under Admin → Users." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="team-performance-table">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-slate-500">
                    <th className="px-5 py-2">Sales Executive</th>
                    <th className="px-3 py-2 text-right">Sales</th>
                    <th className="px-3 py-2 text-right">Pending</th>
                    <th className="px-3 py-2 text-right">Delivered</th>
                    <th className="px-3 py-2 text-right">Drafts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {team.rows.map((r) => (
                    <tr key={r.dse_id} data-testid={`team-row-${r.dse_id}`}>
                      <td className="px-5 py-3">
                        <div className="text-sm font-medium text-slate-900">{r.dse_name}</div>
                      </td>
                      <td className="px-3 py-3 text-right font-mono-data text-slate-900">{r.sales}</td>
                      <td className="px-3 py-3 text-right font-mono-data text-amber-700">{r.pending}</td>
                      <td className="px-3 py-3 text-right font-mono-data text-emerald-700">{r.delivered}</td>
                      <td className="px-3 py-3 text-right font-mono-data text-slate-500">{r.drafts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

// ───────── Shared components ─────────

function SlaSummary({ slaStats, title = "SLA Engine · Business Hours" }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <Timer className="h-4 w-4 text-slate-500" />
        <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{title}</h2>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <MetricCard label="On Track" value={slaStats.on_track} icon={Timer} tone="emerald" testid="sla-metric-on-track" />
        <MetricCard label="At Risk" value={slaStats.at_risk} icon={AlertTriangle} tone="amber" testid="sla-metric-at-risk" />
        <MetricCard label="Delayed" value={slaStats.delayed} icon={AlertOctagon} tone="rose" testid="sla-metric-delayed" />
        <MetricCard label="On Hold" value={slaStats.on_hold + slaStats.paused} icon={Pause} tone="slate" testid="sla-metric-on-hold" />
        <MetricCard label="Delivered Late" value={slaStats.completed_late_today} icon={AlertOctagon} tone="rose" testid="sla-metric-closed-late" />
      </div>
    </div>
  );
}

function RoleQueueCard({ title, count, icon: Icon, tone, to, testid }) {
  const tones = {
    primary: "border-primary/20 bg-primary/5",
    amber: "border-amber-200 bg-amber-50",
    rose: "border-rose-200 bg-rose-50",
    emerald: "border-emerald-200 bg-emerald-50",
    sky: "border-sky-200 bg-sky-50",
  };
  return (
    <Link to={to} data-testid={testid}
          className={`group rounded-lg border p-5 transition-all hover:-translate-y-0.5 hover:shadow-md ${tones[tone]}`}>
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-slate-700" />
        <span className="text-sm font-semibold text-slate-700">{title}</span>
      </div>
      <div className="mt-3 flex items-end justify-between">
        <span className="font-display text-3xl font-semibold tracking-tight text-slate-900">{count ?? "—"}</span>
        <span className="text-xs text-slate-600 group-hover:text-primary">Open →</span>
      </div>
    </Link>
  );
}

// ───────── Management-Action Widgets ─────────

// Filter delivery-file rows to just "due today · not yet delivered".
function filterDueToday(rows) {
  const today = new Date().toISOString().slice(0, 10);
  return (rows || []).filter(
    (r) => r.promised_delivery_date === today && r.status !== "delivered",
  );
}

// Deliveries Due Today — compact table widget listing every vehicle whose
// promised_delivery_date is today and hasn't been handed over yet. Respects
// the top-bar location filter (via the parent's locParam).
function DeliveriesDueTodayCard({ rows, locations }) {
  const locMap = useMemo(
    () => Object.fromEntries((locations || []).map((l) => [l.id, l])),
    [locations],
  );
  const total = rows?.length || 0;
  return (
    <Card className="border-slate-200" data-testid="deliveries-due-today-card">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-amber-600" />
          <CardTitle className="font-display text-lg">Deliveries Due Today</CardTitle>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-flex h-6 min-w-[1.75rem] items-center justify-center rounded-full bg-amber-100 px-2 text-xs font-bold text-amber-800"
            data-testid="deliveries-due-today-total"
          >
            {total}
          </span>
          <Link
            to="/delivery-files?tab=due_today"
            className="inline-flex items-center gap-0.5 text-xs font-semibold text-primary hover:underline"
            data-testid="deliveries-due-today-open"
          >
            Open <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {total === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-slate-500"
               data-testid="deliveries-due-today-empty">
            No deliveries promised for today in the selected location(s).
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-slate-500">
                  <th className="px-5 py-2">Location</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">SR / Vehicle</th>
                  <th className="px-3 py-2">Promised Date</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => {
                  const loc = locMap[r.location_id];
                  return (
                    <tr key={r.id} data-testid={`deliveries-due-today-row-${r.id}`}>
                      <td className="px-5 py-2">
                        <div className="inline-flex items-center gap-1 text-xs font-semibold text-slate-800">
                          <MapPin className="h-3 w-3 text-slate-400" />
                          {loc ? `${loc.code} · ${loc.name}` : "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium text-slate-900">
                          {r.customer?.name || "—"}
                        </div>
                        {r.customer?.phone && (
                          <div className="font-mono-data text-[11px] text-slate-500">
                            {r.customer.phone}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-mono-data text-[11px] font-semibold text-slate-800">
                          {r.sr_number || "—"}
                        </div>
                        <div className="truncate text-[11px] text-slate-500">
                          {r.vehicle?.model || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono-data text-xs text-slate-800">
                        {r.promised_delivery_date || "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Link
                          to={`/delivery-files/${r.id}`}
                          className="inline-flex items-center gap-0.5 rounded-md bg-primary/5 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10"
                          data-testid={`deliveries-due-today-open-${r.id}`}
                        >
                          Open <ArrowRight className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t border-slate-100 bg-slate-50 px-5 py-2 text-right text-[11px] font-semibold text-slate-600">
              Total: {total}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Top KPI strip — 7 tiles on desktop, 2 columns on mobile.
function KpiSection({ stats, scope }) {
  const salesLabel = scope === "manager" ? "Team Sales" : "Sales Released";
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4 xl:grid-cols-7">
      <KpiTile label={salesLabel} value={stats.total_sales} icon={FileText} tone="blue"
        testid="metric-total-sales" subtext="Confirmed sales" />
      <KpiTile label="In Progress" value={stats.in_progress} icon={Activity} tone="amber"
        testid="metric-in-progress" subtext="Being processed" />
      <KpiTile label="Ready for Delivery" value={stats.ready_for_delivery} icon={BadgeCheck} tone="emerald"
        testid="metric-ready-delivery" subtext="Awaiting handover" />
      <KpiTile label="Delivered" value={stats.delivered} icon={Truck} tone="green"
        testid="metric-delivered" subtext="Handover done" />
      <KpiTile label="Drafts" value={stats.drafts} icon={ClipboardList} tone="slate"
        testid="metric-drafts" subtext="Unreleased" />
      <KpiTile label="Deliveries Due Today" value={stats.deliveries_due_today ?? 0}
        icon={CalendarClock} tone="amber" testid="metric-due-today"
        subtext="Promised today" to="/delivery-files?tab=due_today" />
      <KpiTile label="Overdue Deliveries" value={stats.overdue_deliveries ?? 0}
        icon={CircleAlert} tone="rose" testid="metric-overdue"
        subtext="Past promise" to="/delivery-files?tab=overdue" />
    </div>
  );
}

function KpiTile({ label, value, icon: Icon, tone = "blue", testid, subtext, to }) {
  const tones = {
    blue:    { bg: "bg-blue-50/70",    icon: "bg-blue-100 text-blue-700",       ring: "ring-blue-100" },
    amber:   { bg: "bg-amber-50/70",   icon: "bg-amber-100 text-amber-700",     ring: "ring-amber-100" },
    emerald: { bg: "bg-emerald-50/70", icon: "bg-emerald-100 text-emerald-700", ring: "ring-emerald-100" },
    green:   { bg: "bg-green-50/70",   icon: "bg-green-100 text-green-700",     ring: "ring-green-100" },
    slate:   { bg: "bg-slate-50",      icon: "bg-slate-200 text-slate-700",     ring: "ring-slate-200" },
    rose:    { bg: "bg-rose-50/70",    icon: "bg-rose-100 text-rose-700",       ring: "ring-rose-100" },
  }[tone] || {};
  const inner = (
    <div
      className={cn(
        "flex h-full items-start gap-3 rounded-xl px-3.5 py-3 ring-1 transition-all hover:-translate-y-0.5 hover:shadow-md",
        tones.bg, tones.ring,
      )}
      data-testid={testid}
    >
      <span className={cn("flex h-9 w-9 flex-none items-center justify-center rounded-lg", tones.icon)}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">{label}</div>
        <div className="mt-0.5 font-display text-3xl font-semibold leading-none tracking-tight text-slate-900">
          {value ?? "—"}
        </div>
        {subtext && <div className="mt-1 text-[10px] text-slate-500">{subtext}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to} className="block h-full">{inner}</Link> : inner;
}

// Severity by pending count — matches the spec's color rules.
function severityFor(count) {
  if (count > 20) return "red";
  if (count > 10) return "orange";
  if (count > 5) return "yellow";
  return "green";
}

const SEVERITY_STYLES = {
  green:  { ring: "ring-emerald-200", pill: "bg-emerald-100 text-emerald-700", label: "Low",      accent: "text-emerald-700" },
  yellow: { ring: "ring-amber-200",   pill: "bg-amber-100 text-amber-700",     label: "Medium",   accent: "text-amber-700" },
  orange: { ring: "ring-orange-300",  pill: "bg-orange-100 text-orange-800",   label: "High",     accent: "text-orange-700" },
  red:    { ring: "ring-rose-300",    pill: "bg-rose-100 text-rose-800",       label: "Critical", accent: "text-rose-700" },
};

const DEPT_ICONS = {
  pdi:         Wrench,
  accounts:    Banknote,
  back_office: ShieldCheck,
  spares:      Package,
  delivery:    Truck,
};

function DepartmentPendencyGrid({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
          Department Pendency
        </h2>
        <span className="text-[11px] text-slate-500">Top location with highest pendency</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {rows.map((r) => (
          <DepartmentPendencyCard key={r.key} row={r} />
        ))}
      </div>
    </section>
  );
}

function DepartmentPendencyCard({ row }) {
  const Icon = DEPT_ICONS[row.key] || ClipboardList;
  const sev = severityFor(row.total);
  const style = SEVERITY_STYLES[sev];
  const locations = row.locations || [];
  return (
    <div
      className={cn(
        "flex flex-col rounded-xl bg-white p-4 ring-1 shadow-sm",
        style.ring,
      )}
      data-testid={`dept-${row.key}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
            <Icon className="h-4 w-4" />
          </span>
          <span className="text-sm font-semibold text-slate-800">{row.label}</span>
        </div>
        <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide", style.pill)}>
          {style.label}
        </span>
      </div>
      <div className="mt-3 font-display text-4xl font-bold leading-none tracking-tight text-slate-900">
        {row.total}
      </div>
      <div className="mt-3 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">By Location</div>
        {locations.length === 0 ? (
          <div className="mt-1 text-[11px] text-slate-400">No pending work</div>
        ) : (
          <ul className="mt-1 space-y-0.5" data-testid={`dept-${row.key}-locations`}>
            {locations.map((l, i) => {
              const isZero = l.count === 0;
              return (
                <li key={l.location_id || i} className="flex items-center justify-between text-xs">
                  <span className={cn("truncate", isZero ? "text-slate-400" : "text-slate-700")}>{l.location_name}</span>
                  <span className={cn(
                    "ml-2 font-mono-data font-semibold tabular-nums",
                    isZero
                      ? "text-slate-300"
                      : (i === 0 ? style.accent : "text-slate-900"),
                  )}>{l.count}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <Link
        to={row.href}
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
        data-testid={`dept-${row.key}-open`}
      >
        Open <ChevronRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

function ActionRequiredCard({ actions, className }) {
  return (
    <Card className={cn("border-slate-200", className)} data-testid="action-required-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 font-display text-base">
          <Zap className="h-4 w-4 text-amber-600" /> Action Required
        </CardTitle>
        <p className="text-xs text-slate-500">Live signals — items that need attention now.</p>
      </CardHeader>
      <CardContent className="p-0">
        {(!actions || actions.length === 0) ? (
          <EmptyRow message="All caught up — no immediate actions." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {actions.map((a) => (
              <li key={a.key}>
                <Link
                  to={a.href}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50"
                  data-testid={`action-${a.key}`}
                >
                  <span className="min-w-0 flex-1 truncate text-xs text-slate-700">{a.label}</span>
                  <span className={cn(
                    "flex-none rounded-full px-2 py-0.5 font-mono-data text-[11px] font-bold",
                    a.tone === "rose" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800",
                  )}>
                    {a.count}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

const WORKFLOW_COLORS = {
  sales:       "bg-sky-500",
  pdi:         "bg-amber-500",
  accounts:    "bg-rose-500",
  back_office: "bg-indigo-500",
  spares:      "bg-purple-500",
  ready:       "bg-emerald-500",
  delivered:   "bg-green-600",
};

function WorkflowSummaryCard({ workflow, className }) {
  if (!workflow) return null;
  const total = workflow.total || 1;
  return (
    <Card className={cn("border-slate-200", className)} data-testid="workflow-summary-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 font-display text-base">
          <Activity className="h-4 w-4 text-primary" /> Workflow Summary
        </CardTitle>
        <p className="text-xs text-slate-500">Current vehicles across each operational stage.</p>
      </CardHeader>
      <CardContent>
        <div className="mb-3 text-[11px] text-slate-500">
          Total Vehicles <span className="ml-1 font-mono-data text-sm font-bold text-slate-900">{workflow.total}</span>
        </div>
        {/* Stacked bar */}
        <div className="mb-4 flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
          {workflow.stages.map((s) => (
            s.count > 0 && (
              <div
                key={s.key}
                className={cn("h-full", WORKFLOW_COLORS[s.key])}
                style={{ width: `${(s.count / total) * 100}%` }}
                title={`${s.label}: ${s.count}`}
              />
            )
          ))}
        </div>
        <ul className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
          {workflow.stages.map((s) => (
            <li key={s.key} className="flex items-center justify-between gap-2 text-xs" data-testid={`workflow-${s.key}`}>
              <span className="flex items-center gap-1.5 text-slate-700">
                <span className={cn("h-2 w-2 rounded-full", WORKFLOW_COLORS[s.key])} />
                {s.label}
              </span>
              <span className="font-mono-data font-semibold text-slate-900">
                {s.count}
                <span className="ml-1 text-[10px] font-normal text-slate-500">
                  {workflow.total ? `${Math.round((s.count / workflow.total) * 100)}%` : "0%"}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function EmptyRow({ message }) {
  return <div className="px-5 py-8 text-center text-sm text-slate-500">{message}</div>;
}

function RecentSalesCard({ sales, locMap, title, className = "" }) {
  return (
    <Card className={`border-slate-200 ${className}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="font-display text-lg">{title}</CardTitle>
        <Link to="/sales" className="text-xs font-semibold text-primary hover:underline" data-testid="view-all-sales-link">
          View all <ArrowRight className="ml-0.5 inline h-3 w-3" />
        </Link>
      </CardHeader>
      <CardContent className="p-0">
        {sales.length === 0 ? (
          <EmptyRow message="No sales in this period." />
        ) : (
          <ul className="divide-y divide-border">
            {sales.map((s) => (
              <li key={s.id}>
                <Link to={`/sales/${s.id}`}
                      className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-slate-50"
                      data-testid={`recent-sale-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-data text-xs font-medium text-slate-900">
                        {s.sr_number || "— Draft —"}
                      </span>
                      <span className="text-xs text-slate-400">·</span>
                      <span className="truncate text-sm text-slate-700">{s.customer?.name}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {s.vehicle?.model} · {locMap[s.location_id]?.code || ""}
                    </div>
                  </div>
                  <SaleStatusPill status={s.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PendingTasksCard({ tasks, title }) {
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {tasks.length === 0 ? (
          <EmptyRow message="No pending tasks. You're all caught up." />
        ) : (
          <ul className="divide-y divide-border">
            {tasks.map((t) => (
              <li key={t.id}>
                <Link to={`/sales/${t.sale_id}`}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50"
                      data-testid={`my-task-${t.id}`}>
                  <div className="flex-1">
                    <div className="font-mono-data text-xs text-slate-900">{t.sr_number || t.sale_id?.slice(0, 8)}</div>
                    <div className="text-xs text-slate-500">{t.type}</div>
                  </div>
                  <Pill tone="danger">Pending</Pill>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
